/**
 * AUTO-UX + AUTO-1/2 — Automations desk (reusable behavior · not an Incident city).
 * Soft≠Live: posture-gated Live buses only; empty stays empty.
 * AUTO-UX: Arm/Enable/Trigger use hash-stay confirm Drawer + blast preview;
 * cross-primary Response handoffs use leave-seat (Response UI zero-diff).
 */
import { useMemo, useState, useEffect } from "react";
import { createRoot } from "react-dom/client";
import { AnimatePresence } from "framer-motion";
import { html, F, Ico, KV, Card, Field, Input, Alert, M, SPRING, SOFT } from "./ui.js";
import { path, useApi, useAction } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import { softResponseErrorOf } from "./response-live.js";
import { confirmLeaveSeat } from "./shell-wayfind.js";
import { setDirtySeat } from "./quality-runtime.js";
import { AutomationsDomainPanel } from "./automations-domain-panel.js";
import { SafetyReflexPanel } from "./safety-reflex-panel.js";

const ACTION_TYPES = Object.freeze([
  { id: "webhook", label: "Webhook" },
  { id: "sms", label: "SMS" },
  { id: "voice_call", label: "Voice call" },
  { id: "email", label: "Email" },
  { id: "telegram", label: "Telegram" },
  { id: "device_cmd", label: "Device command" },
]);

const TRIGGER_KINDS = Object.freeze([
  { id: "metric_threshold", label: "Metric threshold" },
  { id: "state_change", label: "State change" },
  { id: "schedule", label: "Schedule" },
]);

function labelOf(list, id) {
  const hit = list.find((x) => x.id === id);
  return hit ? hit.label : String(id || "—");
}

/** Soft≠Live: missing contract ops must not crash the desk. */
function safeAutoPath(op, opts) {
  try {
    return path(op, opts);
  } catch {
    if (op === "get__automations_status") return "/automations/status";
    // Response alias retired Soft≠ invent dual SoT — primary path only.
    if (op === "get__automations") return "/automations";
    if (op === "post__automations") return "/automations";
    if (op === "patch__automations_p") {
      const id = (opts && opts.pathParams && opts.pathParams[0]) || "";
      return `/automations/${encodeURIComponent(id)}`;
    }
    if (op === "post__automations_p_trigger") {
      const id = (opts && opts.pathParams && opts.pathParams[0]) || "";
      return `/automations/${encodeURIComponent(id)}/trigger`;
    }
    if (op === "get__automations_p_runs") {
      const id = (opts && opts.pathParams && opts.pathParams[0]) || "";
      return `/automations/${encodeURIComponent(id)}/runs`;
    }
    return "";
  }
}

/** Pure: seat chips from Live automations posture. */
export function responseAutomationsSeatOf(post) {
  const p = post && typeof post === "object" ? post : {};
  const lim = (p.limits && typeof p.limits === "object") ? p.limits : {};
  const lMax = Number(lim.list_max);
  const lDef = Number(lim.list_default);
  const rMax = Number(lim.runs_max);
  const rDef = Number(lim.runs_default);
  return {
    listAllowed: p.list_allowed === true,
    createAllowed: p.create_allowed === true,
    mutateAllowed: p.mutate_allowed === true,
    triggerAllowed: p.trigger_allowed === true,
    runsAllowed: p.runs_allowed === true,
    reReadAllowed: p.re_read_allowed === true,
    engineArmed: p.engine_armed === true,
    role: String(p.principal_role || ""),
    detail: p.detail || null,
    enabled: p.enabled === true,
    listLimit:
      Number.isFinite(lDef) && lDef > 0
        ? Math.min(lDef, Number.isFinite(lMax) && lMax > 0 ? lMax : 256)
        : 48,
    runsLimit:
      Number.isFinite(rDef) && rDef > 0
        ? Math.min(rDef, Number.isFinite(rMax) && rMax > 0 ? rMax : 100)
        : 28,
  };
}

/** Pure: clamp catalogue paint — honest empty when sealed. */
export function automationsRowsClampOf(pack, maxN) {
  const items = (pack && (pack.items || pack.automations)) || [];
  const list = Array.isArray(items) ? items : [];
  const n = Number(maxN);
  const cap = Number.isFinite(n) && n > 0 ? Math.floor(n) : 48;
  return list.slice(0, cap);
}

/** Pure: clamp runs paint — honest empty when sealed. */
export function automationRunsClampOf(pack, maxN) {
  const items = (pack && (pack.items || pack.runs)) || [];
  const list = Array.isArray(items) ? items : [];
  const n = Number(maxN);
  const cap = Number.isFinite(n) && n > 0 ? Math.floor(n) : 28;
  return list.slice(0, cap);
}

function blastLinesOf(selected, selectedId, seat) {
  const ver = selected && selected.version != null
    ? String(selected.version)
    : (selected && selected.rev != null ? String(selected.rev) : "—");
  return [
    "Rule · " + ((selected && selected.name) || selectedId || "—"),
    "Version · " + ver,
    "When · " + labelOf(TRIGGER_KINDS, selected && selected.trigger_kind),
    "Then · " + labelOf(ACTION_TYPES, selected && selected.action_type),
    "Engine · " + (seat && seat.engineArmed ? "armed" : "off"),
    "Practice · preview only (not armed)",
  ];
}

function AutoSafetyDrawer({ kind, lines, onYes, onNo }) {
  const title = kind === "trigger"
    ? "Confirm trigger"
    : (kind === "enable" ? "Confirm enable · publish" : "Arm safety confirm");
  const goLabel = kind === "trigger"
    ? "Trigger now"
    : (kind === "enable" ? "Enable rule" : "Arm confirm");
  return html`
    <${AnimatePresence}>
      <${M.div}
        key="auto-safety"
        className="scrim cmc-drawer-scrim"
        role="presentation"
        initial=${{ opacity: 0 }}
        animate=${{ opacity: 1 }}
        exit=${{ opacity: 0 }}
        transition=${SOFT}
        onClick=${onNo}>
        <${M.div}
          className="sheetlet cmc-drawer"
          role="dialog"
          aria-modal="true"
          aria-labelledby="auto-safety-title"
          data-cmc-shell="auto-ux"
          data-cmc-auto2="auto-2"
          initial=${{ opacity: 0, y: 18 }}
          animate=${{ opacity: 1, y: 0 }}
          transition=${SPRING}
          onClick=${(e) => e.stopPropagation()}>
          <div className="cmc-drawer__in">
            <header className="cmc-drawer__hd">
              <div className="cmc-drawer__hd-copy">
                <span className="cmc-drawer__k">Automations · safety</span>
                <h3 className="cmc-drawer__title" id="auto-safety-title">${title}</h3>
              </div>
              <button type="button" className="cmc-drawer__x" onClick=${onNo} aria-label="Cancel">
                <${Ico} d="close" w=${14}/>
              </button>
            </header>
            <p className="cmc-drawer__lede">
              Blast preview is descriptive only until a real practice run is armed. Nothing invents a successful run here.
            </p>
            <section className="cmc-drawer__sec" data-cmc-auto2-blast="1">
              ${(lines || []).map((ln, i) => html`
                <div key=${i} className="cmc-drawer__row"><span>Step</span><b>${ln}</b></div>`)}
            </section>
            <footer className="cmc-drawer__ft">
              <button type="button" className="btn btn--ghost" onClick=${onNo}>Cancel</button>
              <button type="button" className="btn btn--go" autofocus onClick=${onYes}
                data-cmc-auto-confirm=${kind}>${goLabel}</button>
            </footer>
          </div>
        <//>
      <//>
    <//>`;
}

/** Hash-stay confirm Drawer — AUTO-UX replaces easy-to-miss inline chip. */
export function confirmAutoSafety({ kind, lines }) {
  return new Promise((resolve) => {
    const host = document.createElement("div");
    host.setAttribute("data-cmc-shell-host", "auto-safety");
    document.body.appendChild(host);
    const root = createRoot(host);
    const finish = (ok) => {
      try { root.unmount(); } catch (_) { /* ignore */ }
      try { host.remove(); } catch (_) { /* ignore */ }
      resolve(!!ok);
    };
    root.render(html`<${AutoSafetyDrawer}
      kind=${kind || "arm"}
      lines=${lines || []}
      onYes=${() => finish(true)}
      onNo=${() => finish(false)}
    />`);
  });
}

export function AutomationsDesk({ toast, focusPane, safetyMode }) {
  const { run, busy, error, envelope } = useAction();
  const posture = useApi(safeAutoPath("get__automations_status"));
  const seat = posture.loading
    ? responseAutomationsSeatOf({ enabled: false, limits: {} })
    : responseAutomationsSeatOf(posture.data);
  const list = useApi(seat.listAllowed ? safeAutoPath("get__automations") : null);
  const items = useMemo(
    () => automationsRowsClampOf(list.data, seat.listLimit),
    [list.data, seat.listLimit],
  );
  const [selectedId, setSelectedId] = useState("");
  const [name, setName] = useState("");
  const [triggerKind, setTriggerKind] = useState("metric_threshold");
  const [actionType, setActionType] = useState("webhook");
  const [confirmArmed, setConfirmArmed] = useState(false);
  const [simNote, setSimNote] = useState("");
  /* QUALITY-1: unsaved create draft marks dirty seat. */
  useEffect(() => {
    const dirty = !!String(name || "").trim();
    setDirtySeat(dirty);
    return () => { try { setDirtySeat(false); } catch (_) { /* ignore */ } };
  }, [name]);
  const selected = items.find((it) => String(it.id || it.automation_id || "") === selectedId) || null;
  const runs = useApi(
    seat.runsAllowed && selectedId
      ? safeAutoPath("get__automations_p_runs", { pathParams: [selectedId] })
      : null,
  );
  const runItems = useMemo(
    () => automationRunsClampOf(runs.data, seat.runsLimit),
    [runs.data, seat.runsLimit],
  );
  const focus = String(focusPane || "library");
  /* Floor tabs already exist — paint one job per pane. exceptions borrows Runs. */
  const pane = focus === "exceptions" ? "runs" : (focus || "library");
  const showWrite = pane === "library";
  const showRuns = pane === "runs";
  const showSafety = pane === "safety";
  const safetyOn = !!safetyMode;
  const off = !seat.engineArmed;
  const denied = !seat.listAllowed && !posture.loading;

  const reloadAll = () => {
    if (!seat.reReadAllowed && !posture.loading) {
      toast && toast("Refresh is closed for this seat", "warn");
      return;
    }
    posture.reload && posture.reload();
    list.reload && list.reload();
    runs.reload && runs.reload();
    toast && toast("Automations re-read", "go");
  };

  const leaveResponse = (path, label) =>
    confirmLeaveSeat({
      fromRoute: "/automations/" + (focus || "library"),
      targetPath: path,
      objectLabel: label || selectedId || "",
    });

  const openBlast = async (kind) => {
    if (!selected) {
      toast && toast("Select an automation first", "warn");
      return false;
    }
    const lines = blastLinesOf(selected, selectedId, seat);
    setSimNote(lines.join(" · "));
    return confirmAutoSafety({ kind, lines });
  };

  const create = async () => {
    if (!seat.createAllowed) {
      toast && toast("Create sealed", "warn");
      return;
    }
    if (!name.trim()) {
      toast && toast("Name required", "warn");
      return;
    }
    const out = await run(safeAutoPath("post__automations"), {
      body: {
        name: name.trim(),
        trigger_kind: triggerKind,
        action_type: actionType,
        enabled: false,
      },
    });
    if (!out) {
      toast && toast((error || "Create refused"), "bad");
      return;
    }
    const id = String(
      out.id
      || out.automation_id
      || (out.item && (out.item.id || out.item.automation_id))
      || "",
    );
    list.reload && list.reload();
    if (id) setSelectedId(id);
    setName("");
    toast && toast("Automation created disabled · " + (id || "ok") + " — Enable is a separate action", "go");
  };

  const trigger = async () => {
    if (!seat.triggerAllowed) {
      toast && toast("Trigger sealed", "warn");
      return;
    }
    if (!selectedId) { toast && toast("Select an automation first", "warn"); return; }
    if (!confirmArmed) {
      toast && toast("Arm confirm first. Enable and Trigger stay closed until this rule is confirmed.", "warn");
      return;
    }
    const ok = await openBlast("trigger");
    if (!ok) return;
    const out = await run(safeAutoPath("post__automations_p_trigger", { pathParams: [selectedId] }), {
      body: { source: "cmc_auto2_manual", dry_run: false },
    });
    if (!out) {
      toast && toast((error || "Trigger refused") + " — success is never invented here", "bad");
      return;
    }
    setConfirmArmed(false);
    runs.reload && runs.reload();
    toast && toast("Triggered", "go");
  };

  const setEnabled = async (enabled) => {
    if (!seat.mutateAllowed) {
      toast && toast("Mutate sealed", "warn");
      return;
    }
    if (!selectedId) { toast && toast("Select an automation first", "warn"); return; }
    if (enabled && !confirmArmed) {
      toast && toast("Arm confirm first. Enable and Trigger stay closed until this rule is confirmed.", "warn");
      return;
    }
    if (enabled) {
      const ok = await openBlast("enable");
      if (!ok) return;
    }
    const out = await run(safeAutoPath("patch__automations_p", { pathParams: [selectedId] }), {
      method: "PATCH",
      body: { enabled: !!enabled },
    });
    if (!out) {
      toast && toast((error || "Enable write refused"), "bad");
      return;
    }
    setConfirmArmed(false);
    list.reload && list.reload();
    toast && toast(enabled ? "Enabled" : "Disabled", enabled ? "go" : "warn");
  };

  const armSafety = async () => {
    if (!selected) {
      toast && toast("Select an automation first", "warn");
      return;
    }
    const ok = await openBlast("arm");
    if (!ok) return;
    setConfirmArmed(true);
    toast && toast("Safety confirm recorded for this rule", "go");
  };

  const runDrySim = async () => {
    if (!selected) {
      toast && toast("Select an automation first", "warn");
      return;
    }
    const lines = blastLinesOf(selected, selectedId, seat);
    setSimNote(lines.join(" · "));
    await confirmAutoSafety({ kind: "arm", lines });
    toast && toast("Blast preview only — practice run not armed", "warn");
  };

  const title = off
    ? "Automation engine not armed"
    : (denied
      ? "Automations gated on this account"
      : "Automations desk");
  const say = off
    ? "The engine is off for this seat. Rules stay empty until the engine is armed."
    : (!seat.listAllowed
      ? "Your seat cannot list automations. Ask a manager if you need access."
      : "List, create, enable or disable, manual trigger, and run history when each seat is open.");

  return html`<div
    className="auto-desk auto-desk--liquid"
    data-cmc-auto="page45"
    data-cmc-auto-pin="v604"
    data-cmc-page="page100-automations"
    data-cmc-residual="page185-automations"
    data-cmc-shell="auto-ux"
    data-cmc-auto2="auto-2"
    data-focus=${focus}>
    <${Alert} tone=${off ? "warn" : (denied ? "info" : "info")}
      title=${title}
      say=${say}/>

    <div className="auto-desk__rail">
      <button type="button" className="btn btn--ghost btn--sm"
        disabled=${busy || list.loading || (!seat.reReadAllowed && !posture.loading)}
        onClick=${reloadAll}>
        <${Ico} d="refresh" w=${13}/>Refresh</button>
      ${showWrite ? html`<button type="button" className="btn btn--ghost btn--sm"
        onClick=${() => leaveResponse("/response/rules", "Compat rules")}>
        Compat rules</button>` : null}
      <${KV} k="Automations" v=${!seat.listAllowed ? "—" : (list.loading && !list.data ? "…" : String(items.length))}
        tone=${items.length ? "hi" : "dim"}/>
      <${KV} k="Engine" v=${seat.engineArmed ? "armed" : "off"} tone=${seat.engineArmed ? "hi" : "warn"}/>
      <${KV} k="Seat" v=${seat.role || "—"}/>
      ${envelope ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>` : null}
      ${selectedId ? html`<${KV} k="Selected" v=${selectedId.slice(0, 18)} tone="hi"/>` : null}
    </div>

    ${showWrite ? html`<div className="auto-desk__domain" data-cmc-auto-write="1">
      <${AutomationsDomainPanel} toast=${toast} engineArmed=${seat.engineArmed} createAllowed=${seat.createAllowed} mutateAllowed=${seat.mutateAllowed} triggerAllowed=${seat.triggerAllowed}/>
    </div>` : null}

    <div className="auto-desk__grid" data-cmc-auto-compat=${showWrite ? "1" : "0"}>
      ${(showWrite || showRuns || showSafety) ? html`<${Card} title="Automations" meta=${showWrite ? "compatibility playbooks" : "pick a rule"}>
        ${!seat.listAllowed
          ? html`<p className="note">List not available for your seat.</p>`
          : list.loading && !list.data
            ? html`<p className="note">Loading…</p>`
            : list.error
              ? html`<${Alert} tone=${denied ? "info" : "warn"}
                  title="Automations unavailable" say=${softResponseErrorOf(list.error + "")}/>`
              : html`<div className="rows">
                  ${!items.length
                    ? html`<p className="note">No automations yet.</p>`
                    : items.map((it) => {
                        const id = String(it.id || it.automation_id || "");
                        const on = selectedId === id;
                        return html`
                          <button key=${id} type="button" className="row" data-state=${on ? "on" : "off"}
                            onClick=${()=>{ setSelectedId(id); setConfirmArmed(false); }}>
                            <span className="st"/>
                            <span className="main">
                              <b>${it.name || id || "—"}</b>
                              <span>${labelOf(TRIGGER_KINDS, it.trigger_kind)} → ${labelOf(ACTION_TYPES, it.action_type)}
                                · ${it.enabled === false ? "off" : "on"}</span>
                            </span>
                            <em className="auto-desk__seal" data-on=${it.enabled === false ? "0" : "1"}>
                              ${it.enabled === false ? "off" : "on"}</em>
                          </button>`;
                      })}
                </div>`}
      <//>` : null}

      ${showWrite ? html`<${Card} title="Create automation" meta="create rule">
        ${!seat.createAllowed
          ? html`<p className="note">Creating is not allowed for your seat.</p>`
          : html`<${F}>
            <${Field} label="Name">
              <${Input} icon="book" value=${name}
                onChange=${(e)=>setName(e.target.value)}
                placeholder="Night alarm → SMS"/>
            <//>
            <${Field} label="When">
              <select className="inp" value=${triggerKind}
                onChange=${(e)=>setTriggerKind(e.target.value)}>
                ${TRIGGER_KINDS.map((t) => html`<option key=${t.id} value=${t.id}>${t.label}</option>`)}
              </select>
            <//>
            <${Field} label="Then">
              <select className="inp" value=${actionType}
                onChange=${(e)=>setActionType(e.target.value)}>
                ${ACTION_TYPES.map((t) => html`<option key=${t.id} value=${t.id}>${t.label}</option>`)}
              </select>
            <//>
            <p className="note">Lands disabled. Enable is a separate action. Rule ids are tenant-local — they cannot steal another tenant. Max list ${seat.listLimit}.</p>
            <button type="button" className="btn btn--go btn--sm"
              disabled=${busy || !name.trim() || !seat.createAllowed}
              onClick=${create} style=${{ marginTop:8 }}>
              ${busy ? "Saving…" : "Create · lands off"}</button>
            ${error ? html`<${Alert} tone="bad" title="Could not save automation" say=${softResponseErrorOf(error)}/>` : null}
          <//>`}
      <//>` : null}
    </div>

    ${showSafety ? html`<${Card} title="Selected rule" meta=${selectedId || "pick a rule"} cls="mt" data-cmc-auto2="auto-2">
      ${!selected
        ? html`<p className="note">${seat.listAllowed
          ? "Select an automation to enable, disable, or trigger."
          : "Not available for your seat."}</p>`
        : html`<${F}>
            <${KV} k="Name" v=${selected.name || selectedId}/>
            <${KV} k="Version" v=${selected.version != null ? String(selected.version) : (selected.rev != null ? String(selected.rev) : "—")}/>
            <${KV} k="When" v=${labelOf(TRIGGER_KINDS, selected.trigger_kind)}/>
            <${KV} k="Then" v=${labelOf(ACTION_TYPES, selected.action_type)}/>
            <${KV} k="Compatibility flag"
              v=${selected.enabled === false ? "off" : "on · legacy · not domain Live"}
              tone=${selected.enabled === false ? "warn" : "warn"}/>
            <${Alert} tone="info" title="Legacy rule · not Domain Live Enabled"
              say="This flag is compatibility dispatch_rules only Soft≠ invent Domain activation. Live eligibility is Automations Library (DEGRADED / NEEDS_REVALIDATION never paint Enabled)."/>
            <${Alert} tone="warn" title="AUTO-2 · safety · publish / trigger"
              say="Enable and Trigger open a confirm drawer with blast preview. Simulation results are never invented here. Domain activations never paint Enabled/Live when DEGRADED or NEEDS_REVALIDATION."/>
            <div className="btn-row auto-desk__acts" style=${{ marginTop:8, flexWrap:"wrap", gap:8 }}>
              <button type="button" className="btn btn--ghost btn--sm"
                data-on=${confirmArmed ? "1" : "0"}
                onClick=${armSafety}>
                ${confirmArmed ? "Confirm armed" : "Arm confirm"}</button>
              <button type="button" className="btn btn--ghost btn--sm"
                disabled=${!selectedId}
                onClick=${runDrySim}>Blast preview</button>
              <button type="button" className="btn btn--ghost btn--sm"
                disabled=${busy || !seat.mutateAllowed || !confirmArmed}
                onClick=${()=>setEnabled(true)}>Enable · publish</button>
              <button type="button" className="btn btn--ghost btn--sm"
                disabled=${busy || !seat.mutateAllowed}
                onClick=${()=>setEnabled(false)}>Disable</button>
              <button type="button" className="btn btn--go btn--sm"
                disabled=${busy || !seat.triggerAllowed || !confirmArmed}
                onClick=${trigger}>${busy ? "Triggering…" : "Trigger now"}</button>
            </div>
            ${simNote ? html`<p className="note" data-cmc-auto2-blast="1">${simNote}</p>` : null}
            ${!confirmArmed && (seat.mutateAllowed || seat.triggerAllowed)
              ? html`<p className="note">Arm confirm first. Enable and Trigger stay closed until this rule is confirmed.</p>`
              : null}
            ${!seat.mutateAllowed || !seat.triggerAllowed
              ? html`<p className="note">Writes are not available for your seat.</p>`
              : null}
            ${safetyOn ? html`<p className="note">Safety pane · propose-then-confirm is mandatory.</p>` : null}
          <//>`}
    <//>` : null}

    ${showRuns ? html`<${Card} title="Runs" meta=${selectedId
      ? ("runs · " + selectedId.slice(0, 14))
      : "select a rule"} cls="mt">
      ${!seat.runsAllowed
        ? html`<p className="note">Run history sealed.</p>`
        : !selectedId
          ? html`<p className="note">Select an automation to load runs.</p>`
          : runs.loading && !runs.data
            ? html`<p className="note">Loading runs…</p>`
            : runs.error
              ? html`<${Alert} tone="warn" title="Runs unavailable" say=${runs.error + ""}/>`
              : html`<div className="rows">
                  ${!runItems.length
                    ? html`<p className="note">No runs yet — empty stays empty.</p>`
                    : runItems.map((r, i) => html`
                        <div key=${r.id || i} className="row row--say">
                          <${Ico} d="bolt" w=${14}/>
                          <span className="main">
                            <b>${r.status || r.result || r.ok || "run"}</b>
                            <span>${r.created_at || r.ts || r.started_at || "—"}
                              · ${r.dry_run || r.mode === "dry_run" ? "practice run" : "live run"}
                              · ${r.detail || r.message || r.error || ""}</span>
                          </span>
                        </div>`)}
                </div>`}
      <p className="note">Manual trigger only after a successful engine post. Run history shows up to ${seat.runsLimit} rows.</p>
    <//>` : null}

    ${showSafety ? html`<div className="auto-desk__domain mt" data-cmc-auto-safety="1">
      <${SafetyReflexPanel} toast=${toast}/>
    </div>` : null}
  </div>`;
}

export default AutomationsDesk;
